import React from 'react';
import {enumerate} from 'ts-transformer-enumerate';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';
import parse from 'html-react-parser';

import {Badge} from 'primereact/badge';
import {
    Button,
    ButtonProps
} from 'primereact/button';
import {Panel} from 'primereact/panel';
import {Tooltip} from 'primereact/tooltip';

import {__} from '~/shared/src/utilities/messageCatalog';

import ContentButton, {
    ReferenceType as ContentButtonType,
    ComponentProps as ContentButtonProps
} from '~/frontend/src/components/base/shared/ContentButton';
import {resolveLigatureIcon} from '~/frontend/src/utilities/renderHelpers';

import AddItemDialog, {ComponentProps as AddItemDialogComponentProps} from '~/frontend/src/components/base/ItemFinder/overview/AddItemDialog';
import AdvancedSearchOptionsMenu, {ComponentProps as AdvancedSearchOptionsMenuComponentProps} from '~/frontend/src/components/base/shared/menu/AdvancedSearchOptionsMenu'
import ColumnConfigMenu, {ComponentProps as ColumnConfigMenuProps} from '~/frontend/src/components/base/shared/menu/ColumnConfigMenu';
import ColumnSortMenu, {ComponentProps as ColumnSortMenuProps} from '~/frontend/src/components/base/shared/menu/ColumnSortMenu';
import CopyXmlMenu, {ComponentProps as CopyXmlMenuProps} from '~/frontend/src/components/base/shared/menu/PropertiesAndReferencesSelectionMenu/CopyXmlMenu';
import DownloadXmlMenu, {ComponentProps as DownloadXmlMenuProps} from '~/frontend/src/components/base/shared/menu/PropertiesAndReferencesSelectionMenu/DownloadXmlMenu';
import ItemTypeDropdown, {ComponentProps as ItemTypeDropdownComponentProps} from '~/frontend/src/components/base/shared/field/ItemTypeDropdown';
import PanelSelectorDropdown, {ComponentProps as PanelSelectorDropdownComponentProps} from '~/frontend/src/components/base/shared/field/PanelSelectorDropdown';
import SchemaDropdown, {ComponentProps as SchemaDropdownComponentProps} from '~/frontend/src/components/base/shared/field/SchemaDropdown';
import SearchField, {ComponentProps as SearchFieldProps} from '~/frontend/src/components/base/shared/field/SearchField';
import SearchItemDropdown, {ComponentProps as SearchItemDropdownProps} from '~/frontend/src/components/base/shared/field/SearchItemDropdown';
import ViewSelectorDropdown, {ComponentProps as ViewSelectorDropdownProps} from '~/frontend/src/components/base/shared/field/ViewSelectorDropdown';
import ViewOptionsMenu, {ComponentProps as ViewOptionsMenuProps} from '~/frontend/src/components/base/shared/menu/ViewOptionsMenu';


const contentButtonComponentProps  = keys<ContentButtonProps>(),
      schemaDropdownComponentProps = keys<SchemaDropdownComponentProps>();

type AddButtonComponent = { type : 'addButton'; params : { key : string } & ButtonProps };
type AddItemDialogComponent = { type : 'addItemDialog'; params : { key : string } & AddItemDialogComponentProps };
type AdvancedSearchOptionsMenuComponent = {
    type : 'advancedSearchOptionsMenu';
    params : { key : string } & AdvancedSearchOptionsMenuComponentProps
};
type ButtonComponent = { type : 'button'; params : { key : string } & ButtonProps };
type ButtonSetComponent = { type : 'buttonSet'; params : { key : string, label? : string; buttons : ButtonComponent[] } };
type ClearSearchButtonComponent = { type : 'clearSearchButton'; params : { key : string } & ButtonProps };
type CloneButtonComponent = { type : 'cloneButton'; params : { key : string } & ButtonProps };
type ColumnConfigMenuComponent = { type : 'columnConfigMenu'; params : { key : string } & Omit<ColumnConfigMenuProps, 'id'> };
type ColumnSortMenuComponent = { type : 'columnSortMenu'; params : { key : string } & Omit<ColumnSortMenuProps, 'id'> };
type CopyXmlMenuComponent = { type : 'copyXml'; params : { key : string } & Omit<CopyXmlMenuProps, 'id'> };
type CustomComponent = { type : 'custom'; params : { key : string, className? : string, component : JSX.Element } };
type DeleteButtonComponent = {
    type : 'deleteButton';
    params : {
        key : string;
        confirm : boolean | ( () => boolean );
        onClick : ( event : React.MouseEvent<HTMLButtonElement> ) => void;
        overrideConfirmWithAltKey? : boolean;
        title? : React.ReactNode;
        message? : React.ReactNode;
    } & Omit<ContentButtonProps, 'id' | 'content'>
};
type DownloadXlsxMenuComponent = { type : 'downloadXlsx'; params : { key : string } & ButtonProps };
type DownloadXmlMenuComponent = { type : 'downloadXml'; params : { key : string } & Omit<DownloadXmlMenuProps, 'id'> };
type DummyComponent = { type : 'dummy'; }; // !!! DO NOT REMOVE !!! - needed for typescript
type GenericNodeComponent = { type : 'genericNode'; params : React.ReactNode; };
type ItemTypeDropdownComponent = { type : 'itemTypeDropdown'; params : { key : string } & ItemTypeDropdownComponentProps };
type PanelSelectorDropdownComponent = { type : 'panelSelectorDropdown'; params : { key : string } & PanelSelectorDropdownComponentProps };
type ReloadButtonComponent = {
    type : 'reloadButton';
    params : {
        key : string;
        confirm : boolean | ( () => boolean );
        onClick : ( event : React.MouseEvent<HTMLButtonElement> ) => void;
        overrideConfirmWithAltKey? : boolean;
        title? : React.ReactNode;
        message? : React.ReactNode;
    } & Omit<ContentButtonProps, 'id' | 'content'>
};
type SaveButtonComponent = {
    type : 'saveButton';
    params : { key : string } & ButtonProps;
    saveButtonErrorCount? : number;
    saveButtonErrorTooltip? : React.ReactNode
};
type SchemaDropdownComponent = { type : 'schemaDropdown'; params : { key : string } & SchemaDropdownComponentProps };
type SearchButtonComponent = { type : 'searchButton'; params : { key : string } & ButtonProps };
type SearchFieldComponent = { type : 'searchField'; params : { key : string } & SearchFieldProps };
type SearchItemDropdownComponent = { type : 'searchItemDropdown'; params : { key : string } & SearchItemDropdownProps; };
type SeparatorComponent = { type : 'separator'; params? : { className : string }; };
type ViewOptionsMenuComponent = { type : 'viewOptionsMenu'; params : { key : string } & Omit<ViewOptionsMenuProps, 'id'>; };
type ViewSelectorComponent = { type : 'viewSelectorDropdown'; params : { key : string } & ViewSelectorDropdownProps; };

export type LayoutComponent = AddButtonComponent
    | AddItemDialogComponent
    | AdvancedSearchOptionsMenuComponent
    | ButtonComponent
    | ButtonSetComponent
    | ClearSearchButtonComponent
    | CloneButtonComponent
    | ColumnConfigMenuComponent
    | ColumnSortMenuComponent
    | CopyXmlMenuComponent
    | CustomComponent
    | DeleteButtonComponent
    | DownloadXlsxMenuComponent
    | DownloadXmlMenuComponent
    | DummyComponent
    | GenericNodeComponent
    | ItemTypeDropdownComponent
    | PanelSelectorDropdownComponent
    | ReloadButtonComponent
    | SaveButtonComponent
    | SchemaDropdownComponent
    | SearchButtonComponent
    | SearchFieldComponent
    | SearchItemDropdownComponent
    | SeparatorComponent
    | ViewOptionsMenuComponent
    | ViewSelectorComponent;

export type LayoutComponentType = LayoutComponent[ 'type' ];
export const layoutComponentTypes = enumerate<LayoutComponentType>() as unknown as string[];

export type ToolbarLayoutComponents = {
    left : LayoutComponent[],
    right : LayoutComponent[]
};

export type ToolbarComponents = {
    left : JSX.Element[],
    right : JSX.Element[]
};

export const getComponent = ( layoutComponent : LayoutComponent, additionalClassName : string = '', componentNumber : number = 0 ) : JSX.Element[] => {
    const retVal : JSX.Element[] = [],
          type                   = layoutComponent.type,
          // TODO: How to solve this?
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          params                 = layoutComponent.params,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          errorCount             = layoutComponent.saveButtonErrorCount,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          //@ts-ignore
          errorTooltip           = layoutComponent.saveButtonErrorTooltip;

    let button : React.ReactNode,
        reloadButton : ContentButtonType | undefined = undefined,
        title : React.ReactNode,
        message : React.ReactNode;

    switch ( type ) {
        case 'addButton':
            retVal.push(
                <Button
                    {...params}

                    icon={`pi pi-plus-circle ${params?.icon ?? ''}`}
                    tooltip={params?.tooltip ?? __( 'YN.base.add.button' )}
                    tooltipOptions={config.frontend?.buttonTooltipOptions}
                    className={`p-button-text p-button-success ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'addItemDialog':
            retVal.push(
                <AddItemDialog
                    {...params}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );

            break;

        case 'advancedSearchOptionsMenu':
            retVal.push(
                <AdvancedSearchOptionsMenu
                    {...params}

                    id={`${params.uid}_advancedSearchOptions`}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'button':
            retVal.push(
                <Button
                    {...params}

                    icon={params.icon ? resolveLigatureIcon( params.icon ) : undefined}
                    tooltipOptions={config.frontend?.buttonTooltipOptions}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'buttonSet':
            if ( params.label ) {
                retVal.push(
                    <div className={`flex align-items-center field ${additionalClassName}`}>
                        <label
                            htmlFor={params.key}
                            className="block"
                        >
                            {params.label}
                        </label>
                        <span
                            key={params.key}
                            id={params.key}
                            className={`p-buttonset ${params?.className ?? ''}`}
                        >{params.buttons.map( ( b : ButtonComponent ) => getComponent( b ) )}</span>
                    </div>
                )
            }
            else {
                retVal.push(
                    <span
                        key={params.key}
                        className={`p-buttonset ${params?.className ?? ''} ${additionalClassName}`}
                    >{params.buttons.map( ( b : ButtonComponent ) => getComponent( b ) )}</span>
                );
            }
            break;

        case 'clearSearchButton':
            retVal.push(
                <Button
                    {...params}

                    icon={resolveLigatureIcon( `pi pi-filter-slash ${params?.icon ?? ''}` )}
                    tooltip={params?.tooltip ?? __( 'YN.base.clearSearch.button' )}
                    tooltipOptions={config.frontend?.buttonTooltipOptions}
                    className={`p-button-text ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'cloneButton':
            retVal.push(
                <Button
                    {...params}

                    icon={resolveLigatureIcon( `pi pi-clone ${params?.icon ?? ''}` )}
                    tooltip={params?.tooltip ?? __( 'YN.base.clone.button' )}
                    tooltipOptions={config.frontend?.buttonTooltipOptions}
                    className={`p-button-text ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'columnConfigMenu':
            retVal.push(
                <ColumnConfigMenu
                    {...params}

                    id={`${params.uid}_columnConfig`}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'columnSortMenu':
            retVal.push(
                <ColumnSortMenu
                    {...params}

                    id={`${params.uid}_columnSort`}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'copyXml':
            retVal.push(
                <CopyXmlMenu
                    {...params}

                    id={`${params.uid}_copyXml`}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'custom':
            retVal.push(
                <div
                    key={params.key}

                    className={`${params?.className ?? ''} ${additionalClassName}`}
                >
                    {params.component}
                </div>
            );
            break;

        case 'deleteButton':
            if ( params.title ) {
                title = parse( params.title );
            }
            else {
                title = parse( __( 'YN.base.finder.detail.confirm.deleteItem' ) );
            }

            if ( params.message ) {
                message = parse( params.message );
            }

            retVal.push(
                <ContentButton
                    horizontalContentAlign="right"
                    verticalContentAlign="bottom"

                    {..._.pick( params, contentButtonComponentProps ) as ContentButtonProps}

                    key={params.key}
                    id={params.key}
                    ref={node => {
                        if ( node ) {
                            reloadButton = node;
                        }
                    }}

                    rounded={true}
                    text={true}
                    icon={resolveLigatureIcon( `pi pi-minus-circle ${params?.icon ?? ''}` )}
                    tooltip={params?.tooltip ?? __( 'YN.base.delete.button' )}
                    tooltipOptions={config.frontend?.buttonTooltipOptions}
                    className={`yn-toolbar-button p-button-danger ${params?.className ?? ''} ${additionalClassName}`}
                    contentClassName="yn-toolbar-confirm-button"

                    onExpand={( event ) => {
                        let innerRetVal : boolean = true,
                            confirm : boolean     = true;

                        if ( typeof params.confirm === 'function' ) {
                            confirm = params.confirm();
                        }
                        else {
                            confirm = params.confirm;
                        }

                        if ( !confirm || ( !params.overrideConfirmWithAltKey && event.altKey ) ) {
                            params.onClick( event );

                            innerRetVal = false;
                        }

                        return innerRetVal;
                    }}

                    content={
                        <Panel
                            style={{ width: '25rem' }}
                            className="yn-confirm-option-panel p-0"
                            pt={{
                                content: {
                                    style: {
                                        border: 'none'
                                    }
                                }
                            }}
                        >
                            {title &&
                                <span className="yn-toolbar-confirm-button-title yn-danger">
                                <span className="pi pi-info-circle"></span>
                                    {title}
                            </span>
                            }
                            {message &&
                                <span className="yn-confirm-button-message">
                                {message}
                            </span>
                            }

                            <Button
                                label={__( 'YN.base.common.no' )}
                                className="p-button-sm p-button-secondary mr-3"
                                rounded={false}
                                text={false}
                                outlined={true}
                                onClick={( event ) => {
                                    reloadButton?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                }}
                            />
                            <Button
                                label={__( 'YN.base.common.yes' )}
                                className="p-button-sm p-confirm-popup-accept"
                                rounded={false}
                                text={false}
                                outlined={true}
                                onClick={( event ) => {
                                    params.onClick( event );

                                    reloadButton?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                }}
                            />
                        </Panel>
                    }
                />
            );
            break;

        case 'downloadXml':
            retVal.push(
                <DownloadXmlMenu
                    {...params}

                    id={`${params.uid}_downloadXml`}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'downloadXlsx':
            retVal.push(
                <Button
                    {...params}

                    icon={resolveLigatureIcon( `pi pi-download ${params?.icon ?? ''}` )}
                    tooltip={__( 'YN.base.common.downloadXlsx' )}
                    tooltipOptions={config.frontend?.buttonTooltipOptions}
                    className={`p-button-text yn-download-xlsx-button ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'genericNode':
            retVal.push( params );
            break;

        case 'itemTypeDropdown':
            retVal.push(
                <ItemTypeDropdown
                    {...params}
                    className={`flex align-items-center ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'reloadButton':
            if ( params.title ) {
                title = parse( params.title );
            }
            else {
                title = parse( __( 'YN.base.panel.confirmReload.title' ) );
            }

            if ( params.message ) {
                message = parse( params.message );
            }
            else {
                message = parse( __( 'YN.base.panel.confirmReload.text' ) );
            }

            retVal.push(
                <ContentButton
                    horizontalContentAlign="right"
                    verticalContentAlign="bottom"

                    {..._.pick( params, contentButtonComponentProps ) as ContentButtonProps}

                    key={params.key}
                    id={params.key}
                    ref={node => {
                        if ( node ) {
                            reloadButton = node;
                        }
                    }}

                    rounded={true}
                    text={true}
                    icon={resolveLigatureIcon( `pi pi-refresh ${params?.icon ?? ''}` )}
                    tooltip={params?.tooltip ?? __( 'YN.base.reload.button' )}
                    tooltipOptions={config.frontend?.buttonTooltipOptions}
                    className={`yn-toolbar-button ${params?.className ?? ''} ${additionalClassName}`}
                    contentClassName="yn-toolbar-confirm-button"

                    onExpand={( event ) => {
                        let innerRetVal : boolean = true,
                            confirm : boolean     = true;

                        if ( typeof params.confirm === 'function' ) {
                            confirm = params.confirm();
                        }
                        else {
                            confirm = params.confirm;
                        }

                        if ( !confirm || ( !params.overrideConfirmWithAltKey && event.altKey ) ) {
                            params.onClick( event );

                            innerRetVal = false;
                        }

                        return innerRetVal;
                    }}

                    content={
                        <Panel
                            style={{ width: '25rem' }}
                            className="yn-confirm-option-panel p-0"
                            pt={{
                                content: {
                                    style: {
                                        border: 'none'
                                    }
                                }
                            }}
                        >
                            <span className="yn-toolbar-confirm-button-title yn-warning">
                                <span className="pi pi-exclamation-triangle"></span>
                                {title}
                            </span>
                            <span className="yn-confirm-button-message">
                                {message}
                            </span>

                            <Button
                                label={__( 'YN.base.common.no' )}
                                className="p-button-sm p-button-secondary mr-3"
                                rounded={false}
                                text={false}
                                outlined={true}
                                onClick={( event ) => {
                                    reloadButton?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                }}
                            />
                            <Button
                                label={__( 'YN.base.common.yes' )}
                                className="p-button-sm p-confirm-popup-accept"
                                rounded={false}
                                text={false}
                                outlined={true}
                                onClick={( event ) => {
                                    params.onClick( event );

                                    reloadButton?.collapse( new MouseEvent( 'click' ) as unknown as React.MouseEvent );
                                }}
                            />
                        </Panel>
                    }
                />
            );
            break;

        case 'saveButton':
            button = <Button
                {...params}

                disabled={errorCount ? true : !!params.disabled}
                icon={resolveLigatureIcon( `pi pi-save ${params?.icon ?? ''}` )}
                tooltip={params?.tooltip ?? __( 'YN.base.save.button' )}
                tooltipOptions={config.frontend?.buttonTooltipOptions}
                className={`p-button-text ${params?.className ?? ''} ${additionalClassName}`}
            />;

            if ( errorCount ) {
                if ( errorTooltip ) {
                    retVal.push(
                        <Tooltip
                            key="saveTooltip"
                            target={`#saveButtonError`}
                            position="bottom"
                            autoHide={false}
                            className="yn-tooltip"
                        >
                            {errorTooltip}
                        </Tooltip>
                    );
                    retVal.push(
                        <div key="saveBadge" id="saveButtonError" className="p-overlay-badge">
                            {button}
                            <Badge value={errorCount} severity="danger"></Badge>
                        </div>
                    );
                }
                else {
                    retVal.push(
                        <div key="saveBadge" id="saveButtonError" className="p-overlay-badge">
                            {button}
                            <Badge value={errorCount} severity="danger"></Badge>
                        </div>
                    );
                }
            }
            else {
                retVal.push( button );
            }
            break;

        case 'panelSelectorDropdown':
            retVal.push(
                <PanelSelectorDropdown
                    {...params}
                    className={`flex align-items-center ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'schemaDropdown':
            retVal.push(
                <SchemaDropdown
                    {..._.pick( params, schemaDropdownComponentProps ) as SchemaDropdownComponentProps}

                    className={`flex align-items-center ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'searchButton':
            retVal.push(
                <Button
                    {...params}

                    icon={resolveLigatureIcon( `pi pi-search ${params?.icon ?? ''}` )}
                    tooltip={params?.tooltip ?? __( 'YN.base.search.button' )}
                    tooltipOptions={config.frontend?.buttonTooltipOptions}
                    className={`p-button-text ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'searchField':
            retVal.push(
                <SearchField
                    {...params}
                    className={`flex align-items-center ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'searchItemDropdown':
            retVal.push(
                <SearchItemDropdown
                    {...params}
                    className={`flex align-items-center ${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'separator':
            retVal.push(
                <i key={`separator_${componentNumber}`} className={`p-toolbar-separator ${params?.className ?? ''}`}/>
            );
            break;

        case 'viewOptionsMenu':
            retVal.push(
                <ViewOptionsMenu
                    {...params}

                    id={`${params.uid}_viewOptions`}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;

        case 'viewSelectorDropdown':
            retVal.push(
                <ViewSelectorDropdown
                    {...params}
                    className={`${params?.className ?? ''} ${additionalClassName}`}
                />
            );
            break;
    }

    return retVal;
}

export const getToolbarComponents = ( layoutComponents : ToolbarLayoutComponents ) : ToolbarComponents => {
    const retVal : ToolbarComponents = {
        left : [],
        right: []
    };

    for ( const position of Object.keys( layoutComponents ) ) {
        let lastComponentType = '';

        // remove leading, trailing and multi separators
        for ( let i = layoutComponents[ position ].length - 1; i >= 0; i-- ) {
            if ( layoutComponents[ position ][ i ].type === 'separator' ) {
                // leading
                if ( i === 0 ) {
                    layoutComponents[ position ].splice( i, 1 );
                }
                // trailing
                else if ( i === layoutComponents[ position ].length - 1 ) {
                    layoutComponents[ position ].splice( i, 1 );
                }
                // multiple
                else if ( layoutComponents[ position ][ i - 1 ].type === 'separator' ) {
                    layoutComponents[ position ].splice( i, 1 );
                }
            }
        }

        for ( let i = 0; i < layoutComponents[ position ].length; i++ ) {
            const type = layoutComponents[ position ][ i ].type;

            let additionalClassName = '';

            if ( lastComponentType !== ''
                && lastComponentType !== 'separator'
                && lastComponentType !== 'addButton'
                && lastComponentType !== 'addItemDialog'
                && lastComponentType !== 'button'
                && lastComponentType !== 'clearSearchButton'
                && lastComponentType !== 'cloneButton'
                && lastComponentType !== 'columnConfigMenu'
                && lastComponentType !== 'columnSortMenu'
                && lastComponentType !== 'copyXml'
                && lastComponentType !== 'deleteButton'
                && lastComponentType !== 'downloadXml'
                && lastComponentType !== 'downloadXlsx'
                && lastComponentType !== 'reloadButton'
                && lastComponentType !== 'saveButton'
                && lastComponentType !== 'searchButton'
                && lastComponentType !== 'viewOptionsMenu'
            ) {
                additionalClassName = 'ml-3';
            }
            else if (
                lastComponentType === 'addButton'
                || lastComponentType === 'addItemDialog'
                || lastComponentType === 'button'
                || lastComponentType === 'clearSearchButton'
                || lastComponentType === 'cloneButton'
                || lastComponentType === 'columnConfigMenu'
                || lastComponentType === 'columnSortMenu'
                || lastComponentType === 'copyXml'
                || lastComponentType === 'deleteButton'
                || lastComponentType === 'downloadXml'
                || lastComponentType === 'downloadXlsx'
                || lastComponentType === 'reloadButton'
                || lastComponentType === 'saveButton'
                || lastComponentType === 'searchButton'
                || lastComponentType === 'viewOptionsMenu'
            ) {
                additionalClassName = 'ml-1';
            }

            retVal[ position ] = retVal[ position ].concat( getComponent( layoutComponents[ position ][ i ], additionalClassName, i ) );

            lastComponentType = type;
        }
    }

    return retVal;
}
